import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, worksheetApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

// Preset Question Templates for Instant Form Generation
const CLINICAL_PRESETS = {
  cbt: {
    name: '🧠 CBT Thought Record Template',
    questions: [
      {
        id: 'q_cbt_1',
        question: 'Situation / Triggering Event',
        helpText: 'What was happening right before you noticed a shift in your mood? (Who, what, where, when)',
        fieldType: 'textarea',
        placeholder: 'Describe the situation or trigger...',
        required: true,
        options: [],
      },
      {
        id: 'q_cbt_2',
        question: 'Primary Emotion(s) Experienced',
        helpText: 'Select all emotions that were activated during this event:',
        fieldType: 'checkbox',
        placeholder: '',
        required: true,
        options: ['Anxiety / Worry', 'Sadness / Depression', 'Anger / Frustration', 'Guilt / Shame', 'Loneliness', 'Fear / Panic', 'Other'],
      },
      {
        id: 'q_cbt_3',
        question: 'Emotion Intensity Rating (1 - 10)',
        helpText: 'Rate how strongly you felt this emotion (1 = Very Mild, 10 = Overwhelming)',
        fieldType: 'scale',
        placeholder: '',
        required: true,
        options: [],
      },
      {
        id: 'q_cbt_4',
        question: 'Automatic Negative Thought (ANT)',
        helpText: 'What thoughts or images went through your mind? What were you telling yourself?',
        fieldType: 'textarea',
        placeholder: 'e.g. "I am going to fail and everyone will judge me..."',
        required: true,
        options: [],
      },
      {
        id: 'q_cbt_5',
        question: 'Identified Cognitive Distortion(s)',
        helpText: 'Which thinking traps are present in this thought?',
        fieldType: 'checkbox',
        placeholder: '',
        required: false,
        options: [
          'All-or-Nothing Thinking',
          'Catastrophizing',
          'Mind Reading',
          'Emotional Reasoning',
          'Overgeneralization',
          'Should Statements',
          'Mental Filter',
          'Personalization',
        ],
      },
      {
        id: 'q_cbt_6',
        question: 'Evidence Supporting the Thought',
        helpText: 'What factual, objective evidence supports this automatic thought?',
        fieldType: 'textarea',
        placeholder: 'Facts that support...',
        required: false,
        options: [],
      },
      {
        id: 'q_cbt_7',
        question: 'Evidence Against the Thought',
        helpText: 'What factual evidence contradicts or weakens this thought?',
        fieldType: 'textarea',
        placeholder: 'Facts that contradict...',
        required: true,
        options: [],
      },
      {
        id: 'q_cbt_8',
        question: 'Balanced / Rational Alternative Perspective',
        helpText: 'Write a realistic, compassionate, and balanced reframe of the situation:',
        fieldType: 'textarea',
        placeholder: 'A more realistic and balanced perspective...',
        required: true,
        options: [],
      },
    ],
  },
  mood: {
    name: '📊 Daily Mood & Regulation Log',
    questions: [
      {
        id: 'q_mood_1',
        question: 'Date of Check-in',
        helpText: 'Select the date for this assessment entry',
        fieldType: 'date',
        placeholder: '',
        required: true,
        options: [],
      },
      {
        id: 'q_mood_2',
        question: 'Overall Morning Mood Rating',
        helpText: 'How would you describe your baseline mood today?',
        fieldType: 'radio',
        placeholder: '',
        required: true,
        options: ['Excellent / Energized', 'Good / Calm', 'Neutral / Stable', 'Low / Down', 'Very Low / Distressed'],
      },
      {
        id: 'q_mood_3',
        question: 'Sleep Quality Last Night',
        helpText: 'Approximate hours and perceived quality of sleep',
        fieldType: 'textbox',
        placeholder: 'e.g. 7.5 hours, woke up twice, felt moderately rested',
        required: false,
        options: [],
      },
      {
        id: 'q_mood_4',
        question: 'Physical Sensations Noticed',
        helpText: 'Check all physical somatic sensations present in your body:',
        fieldType: 'checkbox',
        placeholder: '',
        required: false,
        options: ['Chest Tightness', 'Muscle Tension', 'Fatigue / Low Energy', 'Headache', 'Stomach / GI Discomfort', 'Restlessness', 'Normal / Relaxed'],
      },
      {
        id: 'q_mood_5',
        question: 'Coping Strategies Practiced Today',
        helpText: 'What self-care or regulatory tools did you use?',
        fieldType: 'textarea',
        placeholder: 'e.g. 10-minute box breathing, 20-minute walk, journaling, talking to a friend...',
        required: false,
        options: [],
      },
    ],
  },
  anxiety: {
    name: '⚡ Anxiety & Panic Attack Assessment',
    questions: [
      {
        id: 'q_anx_1',
        question: 'Current Anxiety Severity Level',
        helpText: 'Select the severity level of your anxiety right now:',
        fieldType: 'radio',
        placeholder: '',
        required: true,
        options: ['0 - None (Completely Calm)', '1-3 - Mild (Manageable tension)', '4-6 - Moderate (Noticeable interference)', '7-8 - High (Severe distress)', '9-10 - Extreme (Panic / Overwhelm)'],
      },
      {
        id: 'q_anx_2',
        question: 'Primary Anxiety Triggers',
        helpText: 'What specific circumstance or thought pattern triggered this episode?',
        fieldType: 'textarea',
        placeholder: 'Describe the trigger in detail...',
        required: true,
        options: [],
      },
      {
        id: 'q_anx_3',
        question: 'Panic Symptoms Present',
        helpText: 'Select any acute symptoms experienced:',
        fieldType: 'checkbox',
        placeholder: '',
        required: false,
        options: ['Rapid Heart Rate', 'Shortness of Breath', 'Dizziness / Lightheadedness', 'Sweating / Shaking', 'Feeling Detached (Dissociation)', 'Fear of Losing Control'],
      },
      {
        id: 'q_anx_4',
        question: 'Grounding Technique Utilized',
        helpText: 'Which grounding or somatic intervention did you deploy?',
        fieldType: 'select',
        placeholder: 'Select a grounding tool...',
        required: true,
        options: ['5-4-3-2-1 Sensory Grounding', 'Box Breathing (4-4-4-4)', 'Cold Water Splash / Temperature Shift', 'Progressive Muscle Relaxation', 'Safe Place Guided Imagery', 'Cognitive Distraction'],
      },
      {
        id: 'q_anx_5',
        question: 'Post-Intervention Distress Level (1 - 10)',
        helpText: 'Rate your distress level after using the coping tool:',
        fieldType: 'scale',
        placeholder: '',
        required: true,
        options: [],
      },
    ],
  },
};

const FIELD_TYPES = [
  { id: 'textbox', label: 'Textbox (Short Text)', icon: '🔤', desc: 'Single-line text input for short answers' },
  { id: 'textarea', label: 'Textarea (Paragraph)', icon: '📝', desc: 'Multi-line expanded box for detailed responses' },
  { id: 'radio', label: 'Radio (Single Choice)', icon: '🔘', desc: 'Single selection from a list of predefined options' },
  { id: 'checkbox', label: 'Checkbox (Multi Choice)', icon: '☑️', desc: 'Multiple selections from a list of options' },
  { id: 'select', label: 'Select (Dropdown Menu)', icon: '🔻', desc: 'Compact dropdown picker menu' },
  { id: 'scale', label: 'Rating Scale (1 - 10)', icon: '⭐', desc: 'Visual 1 to 10 intensity slider/selector' },
  { id: 'number', label: 'Number Field', icon: '🔢', desc: 'Numeric values, ratings, or hours' },
  { id: 'date', label: 'Date Field', icon: '📅', desc: 'Calendar date picker' },
];

function AdminWorksheetFormBuilderPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  // Worksheets Data
  const [worksheets, setWorksheets] = useState([]);
  const [loadingWorksheets, setLoadingWorksheets] = useState(true);
  const [selectedWorksheetId, setSelectedWorksheetId] = useState('');
  const [activeTab, setActiveTab] = useState('builder'); // 'builder' | 'preview' | 'library'

  // Dynamic Form State for current worksheet
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newOptionInputs, setNewOptionInputs] = useState({}); // { [questionId]: string }

  // Interactive Live Preview Simulation State
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [previewValidated, setPreviewValidated] = useState(false);

  // Notification Toast
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  // Fetch all existing worksheets from library
  const fetchWorksheets = useCallback(async () => {
    try {
      setLoadingWorksheets(true);
      const res = await worksheetApi.adminGetWorksheets({ sortBy: 'newest' });
      if (res?.success) {
        const list = res.worksheets || [];
        setWorksheets(list);
        if (list.length > 0 && !selectedWorksheetId) {
          // Default to first worksheet
          setSelectedWorksheetId(list[0]._id || list[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching worksheets for form builder:', err);
      showFeedback('error', err.message || 'Failed to load worksheets library');
    } finally {
      setLoadingWorksheets(false);
    }
  }, [selectedWorksheetId]);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchWorksheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorksheets, authSession?.token, navigate]);

  // When selected worksheet changes, load its dynamic fields
  useEffect(() => {
    if (!selectedWorksheetId) return;

    const currentWs = worksheets.find((w) => (w._id === selectedWorksheetId || w.id === selectedWorksheetId));
    if (currentWs) {
      if (Array.isArray(currentWs.dynamicFields) && currentWs.dynamicFields.length > 0) {
        setQuestions(currentWs.dynamicFields.map((f, idx) => ({
          ...f,
          id: f.id || `q_${Date.now()}_${idx}`,
          options: Array.isArray(f.options) ? f.options : [],
        })));
      } else {
        // Default with 1 starter question if empty
        setQuestions([
          {
            id: `q_${Date.now()}_1`,
            question: '1. What situation or thought triggered your feelings today?',
            helpText: 'Provide a brief summary of the context or experience.',
            fieldType: 'textarea',
            placeholder: 'Type your response here...',
            required: true,
            options: [],
          },
        ]);
      }
      setPreviewAnswers({});
      setPreviewValidated(false);
    }
  }, [selectedWorksheetId, worksheets]);

  const selectedWorksheet = worksheets.find(
    (w) => w._id === selectedWorksheetId || w.id === selectedWorksheetId
  );

  // Add a new question block
  const handleAddQuestion = (type = 'textarea') => {
    const newQ = {
      id: `q_${Date.now()}_${questions.length + 1}`,
      question: `Question ${questions.length + 1}: `,
      helpText: '',
      fieldType: type,
      placeholder: 'Enter response...',
      required: false,
      options: ['radio', 'checkbox', 'select'].includes(type) ? ['Option 1', 'Option 2', 'Option 3'] : [],
    };
    setQuestions([...questions, newQ]);
    showFeedback('info', 'Added new question field. Customize question text and options.');
  };

  // Delete a question
  const handleDeleteQuestion = (id) => {
    if (questions.length <= 1) {
      showFeedback('error', 'A worksheet form must have at least one question field.');
      return;
    }
    setQuestions(questions.filter((q) => q.id !== id));
    showFeedback('info', 'Question field removed.');
  };

  // Duplicate a question
  const handleDuplicateQuestion = (index) => {
    const target = questions[index];
    const clone = {
      ...target,
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      question: `${target.question} (Copy)`,
      options: [...(target.options || [])],
    };
    const nextList = [...questions];
    nextList.splice(index + 1, 0, clone);
    setQuestions(nextList);
    showFeedback('success', 'Question field duplicated.');
  };

  // Move Question Up/Down
  const handleMoveQuestion = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    const nextList = [...questions];
    const temp = nextList[index];
    nextList[index] = nextList[targetIndex];
    nextList[targetIndex] = temp;
    setQuestions(nextList);
  };

  // Update a field in a specific question
  const handleUpdateQuestion = (id, fieldName, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === id) {
          const updated = { ...q, [fieldName]: value };
          // If fieldType changed to choice type and has no options, seed default options
          if (fieldName === 'fieldType' && ['radio', 'checkbox', 'select'].includes(value) && (!q.options || q.options.length === 0)) {
            updated.options = ['Option 1', 'Option 2', 'Option 3'];
          }
          return updated;
        }
        return q;
      })
    );
  };

  // Add Option to radio/checkbox/select
  const handleAddOption = (questionId) => {
    const rawVal = newOptionInputs[questionId] || '';
    const trimmed = rawVal.trim();
    if (!trimmed) return;

    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [...(q.options || []), trimmed],
          };
        }
        return q;
      })
    );

    setNewOptionInputs((prev) => ({ ...prev, [questionId]: '' }));
  };

  // Remove Option
  const handleRemoveOption = (questionId, optionIndex) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const nextOpts = [...(q.options || [])];
          nextOpts.splice(optionIndex, 1);
          return { ...q, options: nextOpts };
        }
        return q;
      })
    );
  };

  // Load Preset Template
  const handleApplyPreset = (presetKey) => {
    const preset = CLINICAL_PRESETS[presetKey];
    if (!preset) return;

    if (
      questions.length > 1 &&
      !window.confirm(`Replace current fields with "${preset.name}" questions?`)
    ) {
      return;
    }

    setQuestions(
      preset.questions.map((q, idx) => ({
        ...q,
        id: `q_${Date.now()}_${idx}`,
      }))
    );
    showFeedback('success', `Applied preset: ${preset.name}`);
  };

  // Save Dynamic Form to Database
  const handleSaveForm = async () => {
    if (!selectedWorksheetId) {
      showFeedback('error', 'Please select a worksheet from the library first.');
      return;
    }

    if (questions.length === 0) {
      showFeedback('error', 'Please add at least one question to the form.');
      return;
    }

    // Validate that questions have text
    const emptyQ = questions.find((q) => !q.question || !q.question.trim());
    if (emptyQ) {
      showFeedback('error', 'Please ensure all questions have a question title / label.');
      return;
    }

    try {
      setSaving(true);
      const res = await worksheetApi.adminSaveWorksheetForm(selectedWorksheetId, {
        hasDynamicForm: true,
        dynamicFields: questions.map((q, idx) => ({
          ...q,
          order: idx,
        })),
      });

      if (res?.success) {
        showFeedback('success', `Dynamic form saved for "${selectedWorksheet?.title}"! Therapists and clients can now use this interactive form.`);
        
        // Update local worksheets state
        setWorksheets((prev) =>
          prev.map((w) =>
            w._id === selectedWorksheetId || w.id === selectedWorksheetId
              ? { ...w, hasDynamicForm: true, dynamicFields: res.dynamicFields || questions }
              : w
          )
        );
      } else {
        throw new Error(res?.message || 'Failed to save form');
      }
    } catch (err) {
      console.error('Error saving dynamic form:', err);
      showFeedback('error', err.message || 'Failed to save worksheet form');
    } finally {
      setSaving(false);
    }
  };

  // Live Preview Input Change Handler
  const handlePreviewAnswerChange = (qId, value, isCheckbox = false) => {
    setPreviewAnswers((prev) => {
      if (isCheckbox) {
        const curList = Array.isArray(prev[qId]) ? [...prev[qId]] : [];
        if (curList.includes(value)) {
          return { ...prev, [qId]: curList.filter((v) => v !== value) };
        } else {
          return { ...prev, [qId]: [...curList, value] };
        }
      }
      return { ...prev, [qId]: value };
    });
  };

  // Preview form test submission
  const handleTestPreviewSubmit = (e) => {
    e.preventDefault();
    // Validate required fields
    const missing = questions.filter((q) => {
      if (!q.required) return false;
      const ans = previewAnswers[q.id];
      if (q.fieldType === 'checkbox') return !ans || ans.length === 0;
      return !ans || String(ans).trim() === '';
    });

    if (missing.length > 0) {
      setPreviewValidated(false);
      showFeedback('error', `Validation check: Please answer required question "${missing[0].question}" in preview.`);
      return;
    }

    setPreviewValidated(true);
    showFeedback('success', '🎉 Form Validation Successful! All dynamic fields, radios, checkboxes, and text inputs functioned correctly.');
  };

  const totalFormsCount = worksheets.filter((w) => w.hasDynamicForm || (w.dynamicFields && w.dynamicFields.length > 0)).length;
  const totalQuestionsSum = worksheets.reduce((acc, w) => acc + (w.dynamicFields?.length || 0), 0);

  return (
    <div className="admin-dashboard">
      <DashboardSidebar userRole="admin" />

      <main className="admin-dashboard__main" style={{ marginLeft: '280px', padding: '30px', minHeight: '100vh', background: '#f8fafc' }}>
        {/* Toast Notification */}
        {feedback.message && (
          <div
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              zIndex: 9999,
              padding: '16px 24px',
              borderRadius: '12px',
              backgroundColor: feedback.type === 'error' ? '#ef4444' : feedback.type === 'info' ? '#3b82f6' : '#10b981',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '15px',
              fontWeight: 600,
              animation: 'fadeIn 0.3s ease',
            }}
          >
            <span>{feedback.type === 'error' ? '⚠️' : feedback.type === 'info' ? 'ℹ️' : '✅'}</span>
            <span>{feedback.message}</span>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', marginLeft: '10px' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '36px' }}>📋</span>
            <div>
              <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: '#0f172a' }}>
                Worksheet Dynamic Form Builder
              </h1>
              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                Create interactive questionnaire fields (textboxes, textareas, radio buttons, checkboxes, and dropdowns) for worksheets in the library.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setActiveTab('builder')}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: 'none',
                background: activeTab === 'builder' ? '#4f46e5' : 'white',
                color: activeTab === 'builder' ? 'white' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: activeTab === 'builder' ? '0 4px 12px rgba(79, 70, 229, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
              }}
            >
              🔨 Form Builder
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: 'none',
                background: activeTab === 'preview' ? '#4f46e5' : 'white',
                color: activeTab === 'preview' ? 'white' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: activeTab === 'preview' ? '0 4px 12px rgba(79, 70, 229, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
              }}
            >
              👁️ Live Interactive Preview
            </button>
            <button
              onClick={() => setActiveTab('library')}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: 'none',
                background: activeTab === 'library' ? '#4f46e5' : 'white',
                color: activeTab === 'library' ? 'white' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: activeTab === 'library' ? '0 4px 12px rgba(79, 70, 229, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
              }}
            >
              📚 All Forms Overview ({totalFormsCount})
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '16px', marginBottom: '25px' }}>
          <div style={{ background: 'white', padding: '18px 22px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Library Worksheets</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>{worksheets.length}</div>
            <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '2px', fontWeight: 600 }}>Available for form building</div>
          </div>
          <div style={{ background: 'white', padding: '18px 22px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Active Dynamic Forms</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#10b981', marginTop: '6px' }}>{totalFormsCount}</div>
            <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px', fontWeight: 600 }}>Configured with questions</div>
          </div>
          <div style={{ background: 'white', padding: '18px 22px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Current Form Fields</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#6366f1', marginTop: '6px' }}>{questions.length}</div>
            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px', fontWeight: 500 }}>In active worksheet builder</div>
          </div>
          <div style={{ background: 'white', padding: '18px 22px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Question Bank</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#f59e0b', marginTop: '6px' }}>{totalQuestionsSum || questions.length}</div>
            <div style={{ fontSize: '12px', color: '#b45309', marginTop: '2px', fontWeight: 600 }}>Fields across all worksheets</div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* TAB 1: FORM BUILDER */}
        {/* ========================================================================= */}
        {activeTab === 'builder' && (
          <div>
            {/* Step 1: Worksheet Selection Banner */}
            <div
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid #cbd5e1',
                boxShadow: '0 4px 14px rgba(0,0,0,0.04)',
                marginBottom: '25px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1, minWidth: '320px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 800, color: '#1e293b', marginBottom: '6px' }}>
                    1. Select Worksheet from Library to Build / Edit Form:
                  </label>
                  {loadingWorksheets ? (
                    <div style={{ color: '#64748b', fontSize: '14px' }}>Loading worksheets...</div>
                  ) : (
                    <select
                      value={selectedWorksheetId}
                      onChange={(e) => setSelectedWorksheetId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '2px solid #6366f1',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#1e293b',
                        background: 'white',
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {worksheets.map((w) => (
                        <option key={w._id || w.id} value={w._id || w.id}>
                          {w.hasDynamicForm || (w.dynamicFields && w.dynamicFields.length > 0) ? '🟢' : '⚪'}{' '}
                          {w.title} ({w.category || 'Clinical Worksheet'}) — {w.dynamicFields?.length || 0} fields
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Quick Presets Dropdown */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>
                    ⚡ Load Clinical Preset Template:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleApplyPreset('cbt')}
                      style={{
                        padding: '8px 12px',
                        background: '#e0e7ff',
                        color: '#4338ca',
                        border: '1px solid #c7d2fe',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      🧠 CBT Record
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyPreset('mood')}
                      style={{
                        padding: '8px 12px',
                        background: '#ecfdf5',
                        color: '#065f46',
                        border: '1px solid #a7f3d0',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      📊 Mood Log
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyPreset('anxiety')}
                      style={{
                        padding: '8px 12px',
                        background: '#fffbeb',
                        color: '#92400e',
                        border: '1px solid #fde68a',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      ⚡ Anxiety & Panic
                    </button>
                  </div>
                </div>
              </div>

              {/* Selected Worksheet Context Summary */}
              {selectedWorksheet && (
                <div style={{ background: 'white', padding: '14px 18px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '15px' }}>{selectedWorksheet.title}</span>
                    <span style={{ marginLeft: '10px', fontSize: '12px', background: '#ede9fe', color: '#6d28d9', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                      {selectedWorksheet.category}
                    </span>
                    {selectedWorksheet.description && (
                      <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>{selectedWorksheet.description}</p>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>
                    ✓ Ready for dynamic form questions
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Question Blocks List */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1e293b' }}>
                  2. Question Fields ({questions.length})
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleAddQuestion('textarea')}
                    style={{
                      background: '#4f46e5',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span>➕</span> Add Question
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('preview')}
                    style={{
                      background: '#f1f5f9',
                      color: '#334155',
                      border: '1px solid #cbd5e1',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    👁️ Test Live Preview
                  </button>
                </div>
              </div>

              {/* List of Question Field Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {questions.map((q, idx) => {
                  const currentTypeObj = FIELD_TYPES.find((t) => t.id === q.fieldType) || FIELD_TYPES[0];
                  const hasOptions = ['radio', 'checkbox', 'select'].includes(q.fieldType);

                  return (
                    <div
                      key={q.id || idx}
                      style={{
                        background: 'white',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        padding: '22px',
                        transition: 'all 0.2s',
                      }}
                    >
                      {/* Question Header & Order Controls */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span
                            style={{
                              background: '#4f46e5',
                              color: 'white',
                              fontWeight: 800,
                              fontSize: '12px',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Q{idx + 1}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b' }}>
                            {currentTypeObj.icon} {currentTypeObj.label}
                          </span>
                        </div>

                        {/* Reorder and Delete Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => handleMoveQuestion(idx, -1)}
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              cursor: idx === 0 ? 'not-allowed' : 'pointer',
                              color: idx === 0 ? '#cbd5e1' : '#475569',
                              fontSize: '12px',
                            }}
                            title="Move Question Up"
                          >
                            ▲ Up
                          </button>
                          <button
                            type="button"
                            disabled={idx === questions.length - 1}
                            onClick={() => handleMoveQuestion(idx, 1)}
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              cursor: idx === questions.length - 1 ? 'not-allowed' : 'pointer',
                              color: idx === questions.length - 1 ? '#cbd5e1' : '#475569',
                              fontSize: '12px',
                            }}
                            title="Move Question Down"
                          >
                            ▼ Down
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDuplicateQuestion(idx)}
                            style={{
                              background: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              color: '#166534',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                            title="Duplicate this question"
                          >
                            📑 Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteQuestion(q.id)}
                            style={{
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              color: '#991b1b',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                            title="Delete this question"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>

                      {/* Question Content Form */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                        {/* Question Label / Text */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                            Question Title / Label *
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Describe the automatic negative thought you experienced:"
                            value={q.question || ''}
                            onChange={(e) => handleUpdateQuestion(q.id, 'question', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              fontWeight: 600,
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {/* Subtitle / Clinical Help Hint */}
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                            Clinical Subtitle / Help Hint (Optional)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. What specific words or imagery came into your mind right before the anxiety peaked?"
                            value={q.helpText || ''}
                            onChange={(e) => handleUpdateQuestion(q.id, 'helpText', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e1',
                              fontSize: '13px',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {/* Field Type Selector & Required Checkbox */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                          <div style={{ flex: 1, minWidth: '240px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                              Field Type:
                            </label>
                            <select
                              value={q.fieldType}
                              onChange={(e) => handleUpdateQuestion(q.id, 'fieldType', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                background: 'white',
                                outline: 'none',
                                fontWeight: 600,
                              }}
                            >
                              {FIELD_TYPES.map((ft) => (
                                <option key={ft.id} value={ft.id}>
                                  {ft.icon} {ft.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                              Placeholder Text (Optional):
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. Type your answer..."
                              value={q.placeholder || ''}
                              onChange={(e) => handleUpdateQuestion(q.id, 'placeholder', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '22px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                              <input
                                type="checkbox"
                                checked={Boolean(q.required)}
                                onChange={(e) => handleUpdateQuestion(q.id, 'required', e.target.checked)}
                                style={{ width: '16px', height: '16px', accentColor: '#4f46e5', cursor: 'pointer' }}
                              />
                              Required Field *
                            </label>
                          </div>
                        </div>

                        {/* Options Manager (for Radio, Checkbox, Select) */}
                        {hasOptions && (
                          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                                Options / Choices for {q.fieldType === 'radio' ? 'Radio Button' : q.fieldType === 'checkbox' ? 'Checkbox' : 'Dropdown'}:
                              </label>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>
                                {q.options?.length || 0} choices added
                              </span>
                            </div>

                            {/* Existing Options List */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                              {(q.options || []).map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: 'white',
                                    border: '1px solid #cbd5e1',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#1e293b',
                                  }}
                                >
                                  <span>{q.fieldType === 'radio' ? '🔘' : q.fieldType === 'checkbox' ? '☑️' : '•'}</span>
                                  <span>{opt}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveOption(q.id, optIdx)}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      padding: '0 2px',
                                      fontSize: '14px',
                                    }}
                                    title="Remove this option"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>

                            {/* Add Option Input */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                placeholder="Add a new option choice (e.g. Strongly Agree)..."
                                value={newOptionInputs[q.id] || ''}
                                onChange={(e) => setNewOptionInputs({ ...newOptionInputs, [q.id]: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddOption(q.id);
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '13px',
                                  outline: 'none',
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleAddOption(q.id)}
                                style={{
                                  background: '#4f46e5',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                ➕ Add Choice
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom Quick Add Action Bar */}
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', alignSelf: 'center', marginRight: '4px' }}>
                    Quick Append Field:
                  </span>
                  {FIELD_TYPES.slice(0, 5).map((ft) => (
                    <button
                      key={ft.id}
                      type="button"
                      onClick={() => handleAddQuestion(ft.id)}
                      style={{
                        padding: '6px 12px',
                        background: 'white',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: '#334155',
                      }}
                    >
                      {ft.icon} +{ft.label.split(' ')[0]}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Reset questions to blank state?')) {
                        setQuestions([
                          {
                            id: `q_${Date.now()}_1`,
                            question: 'Question 1: ',
                            helpText: '',
                            fieldType: 'textarea',
                            placeholder: 'Enter response...',
                            required: true,
                            options: [],
                          },
                        ]);
                      }
                    }}
                    style={{
                      padding: '12px 20px',
                      background: '#f1f5f9',
                      color: '#64748b',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    🔄 Clear All
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleSaveForm}
                    style={{
                      padding: '12px 28px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '15px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
                    }}
                  >
                    {saving ? '⏳ Saving Form...' : '🚀 Save & Publish Dynamic Form'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: LIVE INTERACTIVE PREVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'preview' && (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ background: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', padding: '32px' }}>
              {/* Preview Header */}
              <div style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '20px', marginBottom: '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', background: '#e0e7ff', color: '#4338ca', padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>
                    👁️ Interactive Patient / Therapist Simulation Mode
                  </span>
                  <button
                    onClick={() => setActiveTab('builder')}
                    style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    ✏️ Back to Editor
                  </button>
                </div>
                <h1 style={{ margin: '8px 0 6px 0', fontSize: '24px', fontWeight: 800, color: '#0f172a' }}>
                  {selectedWorksheet?.title || 'Interactive Worksheet Questionnaire'}
                </h1>
                {selectedWorksheet?.description && (
                  <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                    {selectedWorksheet.description}
                  </p>
                )}
                {selectedWorksheet?.references && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#6366f1', fontStyle: 'italic' }}>
                    📚 Citation: {selectedWorksheet.references}
                  </div>
                )}
              </div>

              {/* Form Render Simulation */}
              <form onSubmit={handleTestPreviewSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {questions.map((q, idx) => (
                    <div key={q.id || idx} style={{ background: '#f8fafc', padding: '18px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                        {idx + 1}. {q.question} {q.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      {q.helpText && (
                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                          {q.helpText}
                        </p>
                      )}

                      {/* Textbox */}
                      {q.fieldType === 'textbox' && (
                        <input
                          type="text"
                          required={q.required}
                          placeholder={q.placeholder || 'Your answer...'}
                          value={previewAnswers[q.id] || ''}
                          onChange={(e) => handlePreviewAnswerChange(q.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            background: 'white',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}

                      {/* Textarea */}
                      {q.fieldType === 'textarea' && (
                        <textarea
                          rows={3}
                          required={q.required}
                          placeholder={q.placeholder || 'Type your reflection or response in detail...'}
                          value={previewAnswers[q.id] || ''}
                          onChange={(e) => handlePreviewAnswerChange(q.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            background: 'white',
                            outline: 'none',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                          }}
                        />
                      )}

                      {/* Radio Option */}
                      {q.fieldType === 'radio' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                          {(q.options || []).map((opt, optIdx) => (
                            <label
                              key={optIdx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 12px',
                                background: previewAnswers[q.id] === opt ? '#e0e7ff' : 'white',
                                border: `1px solid ${previewAnswers[q.id] === opt ? '#6366f1' : '#cbd5e1'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: previewAnswers[q.id] === opt ? 700 : 500,
                                color: previewAnswers[q.id] === opt ? '#3730a3' : '#334155',
                                transition: 'all 0.15s',
                              }}
                            >
                              <input
                                type="radio"
                                name={`radio_${q.id}`}
                                value={opt}
                                checked={previewAnswers[q.id] === opt}
                                onChange={() => handlePreviewAnswerChange(q.id, opt)}
                                style={{ accentColor: '#4f46e5' }}
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Checkbox Option */}
                      {q.fieldType === 'checkbox' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px', marginTop: '6px' }}>
                          {(q.options || []).map((opt, optIdx) => {
                            const isChecked = Array.isArray(previewAnswers[q.id]) && previewAnswers[q.id].includes(opt);
                            return (
                              <label
                                key={optIdx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  padding: '8px 12px',
                                  background: isChecked ? '#e0e7ff' : 'white',
                                  border: `1px solid ${isChecked ? '#6366f1' : '#cbd5e1'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: isChecked ? 700 : 500,
                                  color: isChecked ? '#3730a3' : '#334155',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handlePreviewAnswerChange(q.id, opt, true)}
                                  style={{ accentColor: '#4f46e5' }}
                                />
                                <span>{opt}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {/* Select Dropdown */}
                      {q.fieldType === 'select' && (
                        <select
                          required={q.required}
                          value={previewAnswers[q.id] || ''}
                          onChange={(e) => handlePreviewAnswerChange(q.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            background: 'white',
                            outline: 'none',
                          }}
                        >
                          <option value="">{q.placeholder || '-- Please select an option --'}</option>
                          {(q.options || []).map((opt, optIdx) => (
                            <option key={optIdx} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Rating Scale (1-10) */}
                      {q.fieldType === 'scale' && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>1 (Minimal)</span>
                            <span style={{ fontSize: '13px', fontWeight: 800, color: '#4f46e5' }}>
                              Score: {previewAnswers[q.id] || 5} / 10
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>10 (Maximum)</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            step="1"
                            value={previewAnswers[q.id] || 5}
                            onChange={(e) => handlePreviewAnswerChange(q.id, parseInt(e.target.value, 10))}
                            style={{ width: '100%', accentColor: '#4f46e5', cursor: 'pointer' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 4px', fontSize: '11px', color: '#94a3b8' }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                              <span key={n}>{n}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Number Input */}
                      {q.fieldType === 'number' && (
                        <input
                          type="number"
                          required={q.required}
                          placeholder={q.placeholder || '0'}
                          value={previewAnswers[q.id] || ''}
                          onChange={(e) => handlePreviewAnswerChange(q.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            background: 'white',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}

                      {/* Date Picker */}
                      {q.fieldType === 'date' && (
                        <input
                          type="date"
                          required={q.required}
                          value={previewAnswers[q.id] || ''}
                          onChange={(e) => handlePreviewAnswerChange(q.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            background: 'white',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Validation Test Submit Button */}
                <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setPreviewAnswers({})}
                    style={{
                      padding: '10px 18px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Reset Answers
                  </button>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '12px 24px',
                        background: previewValidated ? '#10b981' : '#4f46e5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                      }}
                    >
                      {previewValidated ? '✅ All Fields Validated (Test Passed)' : '🧪 Test Submit Verification'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveForm}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      💾 Save Form Now
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: ALL WORKSHEET FORMS OVERVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'library' && (
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  All Clinical Worksheet Forms Status
                </h3>
                <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                  Select any worksheet below to customize its questions or review dynamic questionnaires.
                </p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>WORKSHEET NAME</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>CATEGORY</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>FORM STATUS</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600 }}>QUESTIONS COUNT</th>
                  <th style={{ padding: '14px 18px', fontWeight: 600, textAlign: 'right' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {worksheets.map((w) => {
                  const hasForm = w.hasDynamicForm || (w.dynamicFields && w.dynamicFields.length > 0);
                  const count = w.dynamicFields?.length || 0;
                  const isCurrent = (w._id === selectedWorksheetId || w.id === selectedWorksheetId);

                  return (
                    <tr
                      key={w._id || w.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: isCurrent ? '#f5f3ff' : 'white',
                      }}
                    >
                      <td style={{ padding: '16px 18px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>{w.title}</div>
                        {w.description && (
                          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px', maxWidth: '380px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {w.description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px 18px' }}>
                        <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                          {w.category || 'General'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 18px' }}>
                        <span
                          style={{
                            background: hasForm ? '#ecfdf5' : '#fef3c7',
                            color: hasForm ? '#059669' : '#b45309',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}
                        >
                          {hasForm ? '🟢 Form Active' : '⚪ No Form Configured'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 18px', fontWeight: 700, color: '#0f172a' }}>
                        {count} {count === 1 ? 'Question' : 'Questions'}
                      </td>
                      <td style={{ padding: '16px 18px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWorksheetId(w._id || w.id);
                            setActiveTab('builder');
                          }}
                          style={{
                            padding: '6px 14px',
                            background: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          ✏️ Edit Form
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminWorksheetFormBuilderPage;
